import type { paths } from "admination-react/api-schema";
import { ApiError, TransportError, getJson, sendDelete, sendForm, sendJson, type WriteIdentity } from "./http";

export type StoredObject = NonNullable<
  paths["/storage/documents"]["get"]["responses"][200]["content"]["application/json"]
>[number];
export type UploadedObject = NonNullable<
  paths["/storage/documents"]["post"]["responses"][201]["content"]["application/json"]
>;
export type DownloadUrl = NonNullable<
  paths["/storage/documents/{key}"]["get"]["responses"][200]["content"]["application/json"]
>;

export function uploadDocument(file: Blob, filename: string, identity?: WriteIdentity): Promise<UploadedObject> {
  const form = new FormData();
  form.append("file", file, filename);
  return sendForm<UploadedObject>("/storage/documents", form, "POST", identity);
}

export function listDocuments(prefix: string, limit = 200): Promise<StoredObject[]> {
  return getJson<StoredObject[]>("/storage/documents", { prefix, limit });
}

export function documentPath(key: string): string {
  return `/storage/documents/${key.split("/").map(encodeURIComponent).join("/")}`;
}

export function documentUrl(key: string): Promise<DownloadUrl> {
  return getJson<DownloadUrl>(documentPath(key));
}

export async function downloadDocument(key: string): Promise<Blob> {
  const { url } = await documentUrl(key);
  const response = await fetch(url);
  if (!response.ok) throw new Error(`storage GET ${response.status} for ${key}`);
  return response.blob();
}

export function moveDocument(fromKey: string, toKey: string): Promise<UploadedObject> {
  return sendJson<UploadedObject>("/storage/documents/move", "POST", {
    from_key: fromKey,
    to_key: toKey,
  });
}

export function deleteDocument(key: string, identity?: WriteIdentity): Promise<void> {
  return sendDelete(documentPath(key), undefined, identity);
}

export type SigningAssetKind =
  paths["/storage/signing-assets/{kind}/{owner_id}"]["get"]["parameters"]["path"]["kind"];

export type UploadedSigningAsset = NonNullable<
  paths["/storage/signing-assets/{kind}/{owner_id}"]["put"]["responses"][200]["content"]["application/json"]
>;

function signingAssetPath(kind: SigningAssetKind, ownerId: string): string {
  return `/storage/signing-assets/${kind}/${encodeURIComponent(ownerId)}`;
}

export function uploadSigningAsset(
  kind: SigningAssetKind,
  ownerId: string,
  png: Blob,
  identity?: WriteIdentity,
): Promise<UploadedSigningAsset> {
  const form = new FormData();
  form.append("file", png, kind === "member-signature" ? "signature.png" : "stamp.png");
  return sendForm<UploadedSigningAsset>(signingAssetPath(kind, ownerId), form, "PUT", identity);
}

export function signingAssetUrl(kind: SigningAssetKind, ownerId: string): Promise<DownloadUrl> {
  return getJson<DownloadUrl>(signingAssetPath(kind, ownerId));
}

export async function downloadSigningAsset(
  kind: SigningAssetKind,
  ownerId: string
): Promise<Blob> {
  const { url } = await signingAssetUrl(kind, ownerId);
  try {
    const response = await fetch(url);
    if (!response.ok) throw new ApiError(response.status, signingAssetPath(kind, ownerId), undefined);
    return await response.blob();
  } catch (error) {
    if (error instanceof ApiError) throw error;
    throw new TransportError("Signing-Asset konnte nicht geladen werden.");
  }
}
